/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaWorkspaceUrl.ts
 * FILE_ID: JOBHUB-QA-WORKSPACE-URL-001
 * PURPOSE: Persist QA inspection, area, checklist and mode in the URL.
 * WHY: Reload used to land on first area / All checklists / Build, hiding notes.
 */
import { useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { QaAreaMode } from '../JobQaModeToggle'

export interface QaWorkspaceUrlState {
  qaList: string | null
  qaArea: string | null
  qaChecklist: string | null
  qaMode: QaAreaMode | null
  qaAll: boolean
}

const MODES: ReadonlySet<string> = new Set(['build', 'run', 'itp'])

export function readQaWorkspaceUrl(params: URLSearchParams): QaWorkspaceUrlState {
  const mode = params.get('qaMode')
  return {
    qaList: params.get('qaList'),
    qaArea: params.get('qaArea'),
    qaChecklist: params.get('qaChecklist'),
    qaMode: mode && MODES.has(mode) ? (mode as QaAreaMode) : null,
    qaAll: params.get('qaAll') === '1',
  }
}

export function useQaWorkspaceUrl() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initial = useRef(readQaWorkspaceUrl(searchParams)).current

  const write = useCallback((next: QaWorkspaceUrlState) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      const put = (key: string, value: string | null | undefined) => {
        if (value) params.set(key, value)
        else params.delete(key)
      }
      put('qaList', next.qaList)
      put('qaArea', next.qaArea)
      put('qaChecklist', next.qaAll ? null : next.qaChecklist)
      put('qaMode', next.qaMode)
      if (next.qaAll) params.set('qaAll', '1')
      else params.delete('qaAll')
      return params
    }, { replace: true })
  }, [setSearchParams])

  return { initial, write }
}
